import React, { useMemo, useRef, useState } from 'react';
import useElementWidth from './useElementWidth';
import Tooltip from './Tooltip';
import { niceTicks, isNum } from '../format';

const M = { top: 14, right: 16, bottom: 28, left: 8 };

/**
 * Multi-series line chart with a crosshair readout.
 * series: [{ id, label, color, points: [{x: ms, y: number|null}], emphasis?: bool }]
 */
export default function LineChart({
  series = [],
  height = 220,
  yFormat = (v) => String(v),
  xFormat = (ms) => new Date(ms).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
  xTickFormat,
  fromZero = false,
  endLabel = true,
}) {
  const wrapRef = useRef(null);
  const width = useElementWidth(wrapRef);
  const [hover, setHover] = useState(null);

  const model = useMemo(() => {
    const xs = new Set();
    let yMin = Infinity;
    let yMax = -Infinity;
    series.forEach((s) => (s.points || []).forEach((p) => {
      if (!isNum(p.x)) return;
      xs.add(p.x);
      if (isNum(p.y)) { yMin = Math.min(yMin, p.y); yMax = Math.max(yMax, p.y); }
    }));
    const xSorted = Array.from(xs).sort((a, b) => a - b);
    if (xSorted.length === 0 || !Number.isFinite(yMin)) return null;
    if (fromZero) yMin = Math.min(0, yMin);
    const ticks = niceTicks(yMin, yMax, 4);
    const y0 = ticks.length ? ticks[0] : yMin;
    const y1 = ticks.length ? ticks[ticks.length - 1] : yMax;
    // Left margin grows with the widest tick label.
    const labelWidth = Math.max(...ticks.map((t) => yFormat(t).length), 3) * 7 + 10;
    const left = M.left + labelWidth;
    const plotW = Math.max(40, width - left - M.right);
    const plotH = Math.max(40, height - M.top - M.bottom);
    const x0 = xSorted[0];
    const x1 = xSorted[xSorted.length - 1];
    const sx = (x) => left + (x1 === x0 ? plotW / 2 : ((x - x0) / (x1 - x0)) * plotW);
    const sy = (y) => M.top + plotH - ((y - y0) / (y1 - y0 || 1)) * plotH;
    const paths = series.map((s) => {
      const pts = (s.points || []).filter((p) => isNum(p.x)).slice().sort((a, b) => a.x - b.x);
      let d = '';
      let pen = false;
      pts.forEach((p) => {
        if (!isNum(p.y)) { pen = false; return; }
        d += `${pen ? 'L' : 'M'}${sx(p.x).toFixed(1)},${sy(p.y).toFixed(1)}`;
        pen = true;
      });
      const last = [...pts].reverse().find((p) => isNum(p.y)) || null;
      const lookup = new Map(pts.map((p) => [p.x, p.y]));
      return { ...s, d, last, lookup };
    });
    // X ticks: aim for one label per ~90px.
    const tickCount = Math.max(2, Math.floor(plotW / 90));
    const xTicks = [];
    for (let i = 0; i < tickCount; i += 1) {
      const idx = Math.round((i / (tickCount - 1)) * (xSorted.length - 1));
      xTicks.push(xSorted[idx]);
    }
    return { xSorted, ticks, left, plotW, plotH, sx, sy, paths, xTicks, y0, y1 };
  }, [series, width, height, yFormat, fromZero]);

  if (!model) {
    return <div ref={wrapRef} className="le-chart-empty" style={{ height }}>No data to plot</div>;
  }

  const onMove = (evt) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const px = evt.clientX - rect.left;
    const py = evt.clientY - rect.top;
    // nearest x
    let best = model.xSorted[0];
    let bestDist = Infinity;
    for (const x of model.xSorted) {
      const dist = Math.abs(model.sx(x) - px);
      if (dist < bestDist) { bestDist = dist; best = x; }
    }
    setHover({ x: best, px: model.sx(best), py });
  };

  const hoverRows = hover ? model.paths.map((s) => ({
    id: s.id,
    label: s.label,
    color: s.color,
    value: isNum(s.lookup.get(hover.x)) ? yFormat(s.lookup.get(hover.x)) : '—',
  })) : [];

  return (
    <div
      ref={wrapRef}
      className="le-chart le-line-chart"
      style={{ height }}
      onPointerMove={onMove}
      onPointerLeave={() => setHover(null)}
    >
      <svg width={width} height={height} role="img" aria-label="Line chart">
        {model.ticks.map((t) => (
          <g key={t}>
            <line className="le-grid" x1={model.left} x2={model.left + model.plotW} y1={model.sy(t)} y2={model.sy(t)} />
            <text className="le-axis-text" x={model.left - 8} y={model.sy(t) + 4} textAnchor="end">{yFormat(t)}</text>
          </g>
        ))}
        <line className="le-axis" x1={model.left} x2={model.left + model.plotW} y1={M.top + model.plotH} y2={M.top + model.plotH} />
        {model.xTicks.map((x, i) => (
          <text
            key={`${x}-${i}`}
            className="le-axis-text"
            x={model.sx(x)}
            y={height - 8}
            textAnchor={i === 0 ? 'start' : i === model.xTicks.length - 1 ? 'end' : 'middle'}
          >
            {(xTickFormat || xFormat)(x)}
          </text>
        ))}
        {model.paths.map((s) => (
          <path
            key={s.id}
            d={s.d}
            className={`le-line ${s.emphasis ? 'le-line-emphasis' : ''}`}
            style={{ stroke: s.color }}
            fill="none"
          />
        ))}
        {hover ? (
          <line className="le-crosshair" x1={hover.px} x2={hover.px} y1={M.top} y2={M.top + model.plotH} />
        ) : null}
        {model.paths.map((s) => {
          const y = hover ? s.lookup.get(hover.x) : (s.last ? s.last.y : null);
          const x = hover ? hover.x : (s.last ? s.last.x : null);
          if (!isNum(y) || !isNum(x)) return null;
          return (
            <circle key={`dot-${s.id}`} className="le-dot" cx={model.sx(x)} cy={model.sy(y)} r={4} style={{ fill: s.color }} />
          );
        })}
        {endLabel && !hover ? model.paths.filter((s) => s.emphasis && s.last).map((s) => (
          <text
            key={`end-${s.id}`}
            className="le-end-label"
            x={Math.min(model.sx(s.last.x) + 8, model.left + model.plotW)}
            y={model.sy(s.last.y) - 8}
            textAnchor="end"
          >
            {yFormat(s.last.y)}
          </text>
        )) : null}
      </svg>
      {hover ? <Tooltip x={hover.px} y={hover.py} width={width} title={xFormat(hover.x)} rows={hoverRows} /> : null}
    </div>
  );
}
